import { useEffect } from "react";
import ReactGA from "react-ga4";
import { searchCatalog, SearchHit } from "../apis/search-catalog";

export interface PageTrackingGAOptions {
  pathname: string;
  search: string;
  userStatus?: string;
  hashUserCpf?: string;
}

export interface EventTrackingGAOptions {
  category: string;
  action: string;
  label?: string;
  value?: number;
  nonInteraction?: boolean;
  transport?: "beacon" | "image" | "xhr";
}

/**
 * @param location PageTrackingGAOptions
 */
async function trackPageViewGA(location: PageTrackingGAOptions): Promise<void> {
  let externalId: string | undefined;
  let serviceName: string | undefined;

  const splittedPathName = location.pathname?.split("-");

  if (Array.isArray(splittedPathName) && splittedPathName.length) {
    const lastPathPart = splittedPathName[splittedPathName.length - 1];

    const hasExternalId = !isNaN(Number(lastPathPart));

    if (hasExternalId) {
      externalId = lastPathPart;

      const serviceSlug = location.pathname.replace(
        "/app/catalog/carta-de-servicos/",
        ""
      );

      try {
        const currentService = await searchCatalog({
          slugs: [serviceSlug],
        });

        if (currentService?.length) {
          const service = currentService[0] as SearchHit;
          serviceName = service.title;
        }
      } catch (_) {}
    }
  }

  ReactGA.event("page_view", {
    page_path: location.pathname + location.search,
    externalId: externalId ? String(externalId) : undefined,
    serviceName,
    userStatus: location.userStatus ?? "anonymous",
    hashUserCpf: location.hashUserCpf,
  });
}

export function usePageTrackingGA(location: PageTrackingGAOptions): void {
  useEffect(() => {
    trackPageViewGA({
      pathname: location.pathname,
      search: location.search,
      userStatus: location.userStatus,
      hashUserCpf: location.hashUserCpf,
    });
  }, [
    location.pathname,
    location.search,
    location.userStatus,
    location.hashUserCpf,
  ]);

  useEffect(() => {
    function handleCatalogNavigation(event: Event) {
      const customEvent = event as CustomEvent<{
        pathname: string;
        search?: string;
        source?: string;
      }>;

      const { pathname, search = "" } = customEvent.detail;

      trackPageViewGA({
        pathname,
        search,
        userStatus: location.userStatus,
        hashUserCpf: location.hashUserCpf,
      });
    }

    window.addEventListener("catalog:navigation", handleCatalogNavigation);

    return () => {
      window.removeEventListener("catalog:navigation", handleCatalogNavigation);
    };
  }, [location.userStatus, location.hashUserCpf]);
}
